import React, { useEffect, useRef, useState, useCallback } from "react";
import { RadioGroup, Radio } from "@/components/ui/Radio";
import { Stack } from "@/components/ui/Stack";
import { FormControl } from "@/components/ui/FormControl";
import { Input } from "@/components/ui/Input";
import classes from "./BankDetails.module.scss";
import { useDonationFormStore } from "@/stores";
import type { BankDetailsProps } from "./BankDetailsProps";
import { clsx } from "clsx";
import { useTranslation } from "@/hooks/useTranslation";
import { useService } from "@/hooks/useService";
import { trackError } from "@/utils/error-monitoring";
import { Spinner } from "../ui/Spinner";
import type { Option } from "@/types/Form";
import { formatDate } from "@/utils/formatters";
import { BankDetailsSortCode } from "./BankDetailsSortCode";
import { BankDetailsNumberField } from "./BankDetailsNumberField";
import { BankDetailsDirectDebitGuaranteeModal } from "./BankDetailsDirectDebitGuaranteeModal";
import { ValidationGroupProvider } from "@/contexts/ValidationGroupProvider";
import { Field } from "../Field";
import * as z from "zod";
import type { BankDetails as BankDetailsType } from "@/types/Payment";
import type { DonationFormStateValues } from "@/stores";
import { ApiError } from "@/utils/api";

const acountNumberErrors = [
  "Please enter a valid account number",
  "The account is not Direct Debit Capable",
];

function fomatDateParam(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

const today = fomatDateParam(new Date());

export function BankDetails(props: BankDetailsProps) {
  const { config, className, ref } = props;

  const { bankDetails, setBankDetails } = useDonationFormStore();
  const [loading, setLoading] = useState(false);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const disclaimerRef = useRef<HTMLDivElement>(null);
  const [showGuaranteeModal, setShowGuaranteeModal] = useState(false);

  const t = useTranslation("bank_details");
  const { getRetrieveAvailableDates, validateBankDetails } = useService();

  const { start_date } = bankDetails;

  const handleAsyncValidation = useCallback(
    async (currentState: DonationFormStateValues) => {
      let validationResponse: boolean | string;

      try {
        validationResponse = await validateBankDetails({
          account_number: currentState.bankDetails.account_number,
          sort_code: currentState.bankDetails.sort_code,
        });
      } catch (error) {
        if (error instanceof ApiError && error.status === 429) {
          return {
            isValid: false,
            errors: {
              account_number: t("errors.validation-rate-limited") as string,
            },
          };
        }

        // Bank validation is an optional, best-effort check. When the call
        // itself fails there is no verdict to act on, so let the donation
        // through instead of leaving the donor on a step that never resolves.
        trackError(error as Error);
        return { isValid: true };
      }

      if (validationResponse === true) {
        return { isValid: true };
      }

      const errors: Record<string, string> = {};

      if (typeof validationResponse === "boolean") {
        errors.account_number = t("errors.account-number-invalid") as string;
        return { isValid: false, errors };
      }

      if (acountNumberErrors.includes(validationResponse)) {
        errors.account_number = validationResponse;
      } else if (validationResponse === "Please enter a valid sort code") {
        errors.sort_code = validationResponse;
      } else {
        errors.account_number = t("errors.account-number-invalid") as string;
        errors.sort_code = t("errors.sort-code-invalid") as string;
      }

      return { isValid: false, errors };
    },
    [validateBankDetails, t],
  );

  useEffect(() => {
    if (disclaimerRef.current) {
      const link = disclaimerRef.current.querySelector("a");
      if (link) {
        const handleLinkClick = (event: MouseEvent) => {
          event.preventDefault();
          setShowGuaranteeModal(true);
        };

        link.addEventListener("click", handleLinkClick);

        return () => {
          link.removeEventListener("click", handleLinkClick);
        };
      }
    }
  }, [disclaimerRef]);

  useEffect(() => {
    const fetchAvailableDates = async () => {
      setLoading(true);
      try {
        const response = await getRetrieveAvailableDates(today);
        const dates = response["available-dates"];

        if (!Array.isArray(dates) || dates.length === 0) {
          throw new Error("No available dates found");
        }

        setAvailableDates(dates);
        setBankDetails({
          ...bankDetails,
          start_date: dates[0],
        });
      } catch (error) {
        trackError(error as Error);
      } finally {
        setLoading(false);
      }
    };
    fetchAvailableDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getRetrieveAvailableDates, setBankDetails]);

  const handleChangeStartDate = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target;
    setBankDetails({
      ...bankDetails,
      start_date: value,
    });
  };

  const options: Option[] = availableDates.map((date) => {
    const formattedDate = formatDate(date);

    return {
      value: date,
      label: formattedDate,
    };
  });

  return (
    <>
      <BankDetailsDirectDebitGuaranteeModal
        open={showGuaranteeModal}
        onClose={() => setShowGuaranteeModal(false)}
      >
        <div className={classes.formatter}>
          {t(config["bank-details-guarantee"].i18n)}
        </div>
      </BankDetailsDirectDebitGuaranteeModal>

      <ValidationGroupProvider
        name="bankDetails"
        ref={ref}
        validate={handleAsyncValidation}
      >
        <div className={clsx(classes.wrapper, className)}>
          <Stack gap="md">
            <Field<BankDetailsType>
              name="account_holder"
              serverErrorPaths={["bank_details.account_holder"]}
              validate={() => {
                return z
                  .string()
                  .min(1, t("errors.account-holder-required") as string);
              }}
              render={({
                value,
                handleChange,
                handleBlur,
                ref,
                errorMessage,
                isValid,
              }) => (
                <FormControl
                  label={t(config["bank-details-account-holder-label"].i18n)}
                  error={errorMessage}
                  isValid={isValid}
                  required
                >
                  <Input
                    ref={ref as React.RefObject<HTMLInputElement>}
                    name="account_holder"
                    value={value}
                    onChange={(e) => handleChange(e.target.value, e)}
                    onBlur={() => handleBlur()}
                  />
                </FormControl>
              )}
            />

            <Field<BankDetailsType>
              name="account_number"
              serverErrorPaths={["bank_details.account_number"]}
              validate={() => {
                return z
                  .string()
                  .min(1, t("errors.account-number-invalid") as string)
                  .regex(
                    /^\d{8}$/,
                    t("errors.account-number-invalid") as string,
                  );
              }}
              render={({
                value,
                handleChange,
                handleBlur,
                ref,
                errorMessage,
                isValid,
              }) => (
                <FormControl
                  label={t(config["bank-details-account-number-label"].i18n)}
                  error={errorMessage}
                  isValid={isValid}
                  required
                >
                  <BankDetailsNumberField
                    ref={ref as React.RefObject<HTMLInputElement>}
                    name="account_number"
                    value={value}
                    onChange={(newValue) => handleChange(newValue)}
                    maxLength={8}
                    onBlur={() => handleBlur()}
                  />
                </FormControl>
              )}
            />

            <Field<BankDetailsType>
              name="sort_code"
              serverErrorPaths={["bank_details.sort_code"]}
              validate={() => {
                return z
                  .string()
                  .min(1, t("errors.sort-code-invalid") as string)
                  .regex(/^\d{6}$/, t("errors.sort-code-invalid") as string);
              }}
              render={({
                value,
                handleChange,
                handleBlur,
                ref,
                errorMessage,
                isValid,
              }) => (
                <BankDetailsSortCode
                  label={
                    t(config["bank-details-sort-code-label"].i18n) as string
                  }
                  initialValue={value}
                  onChange={(newValue) => handleChange(newValue)}
                  error={errorMessage}
                  isValid={isValid}
                  ref={ref as React.RefObject<{ focus: () => void }>}
                  onBlur={() => handleBlur()}
                />
              )}
            />

            {loading && (
              <div
                style={{
                  height: 112,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Spinner />
              </div>
            )}
            {!loading && options.length > 0 && (
              <FormControl
                type="fieldset"
                label={t(config["bank-details-start-date-label"].i18n)}
                required
              >
                <RadioGroup
                  direction="column"
                  name="start_date"
                  value={start_date}
                  onChange={handleChangeStartDate}
                >
                  {options.map((option) => (
                    <Radio key={option.value} value={option.value}>
                      {option.label}
                    </Radio>
                  ))}
                </RadioGroup>
              </FormControl>
            )}

            <div className={clsx(classes.formatter, classes.withTitle)}>
              {t(config["bank-details-acknowledgement"].i18n, {
                direct_debit_link: `<a href="${config["bank-details-direct-debit-file"]?.src ?? ""}" target="_blank" rel="noopener">Direct Debit Instruction</a>`,
              })}
            </div>

            <div className={classes.formatter} ref={disclaimerRef}>
              {t(config["bank-details-disclaimer"].i18n, {
                linkToDirectDebitGuarantee: "#",
              })}
            </div>
          </Stack>
        </div>
      </ValidationGroupProvider>
    </>
  );
}
